import type { Context } from "../context.js";
import type { EmitFn } from "../adapters.js";

/**
 * Detección automática del artículo (heurística readability) + señales de lectura:
 * scrollDepthPct, readingProgressPct, section_view, read_complete.
 * Todo con listeners passive + throttle a rAF e IntersectionObserver (cero coste
 * perceptible en scroll).
 */

export function detectArticle(): HTMLElement {
  const explicit =
    document.querySelector<HTMLElement>("article") ||
    document.querySelector<HTMLElement>("[itemprop='articleBody']") ||
    document.querySelector<HTMLElement>("main");
  if (explicit) return explicit;
  // Fallback: bloque con mayor cantidad de texto.
  let best: HTMLElement = document.body;
  let bestLen = 0;
  document.querySelectorAll<HTMLElement>("div, section").forEach((el) => {
    const len = el.innerText?.length ?? 0;
    if (len > bestLen) {
      bestLen = len;
      best = el;
    }
  });
  return best;
}

export function startReading(ctx: Context, emit: EmitFn) {
  let article = detectArticle();
  const scrollMilestones = [25, 50, 75, 100];
  let firedScroll = new Set<number>();
  let ticking = false;
  let headingIO: IntersectionObserver | null = null;

  function measure() {
    ticking = false;
    const viewportH = window.innerHeight;
    const docH = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    );
    const scrolled = window.scrollY + viewportH;

    // Scroll depth global.
    const depth = Math.min(100, Math.round((scrolled / docH) * 100));
    if (depth > ctx.state.scrollDepthPct) ctx.state.scrollDepthPct = depth;
    for (const m of scrollMilestones) {
      if (ctx.state.scrollDepthPct >= m && !firedScroll.has(m)) {
        firedScroll.add(m);
        emit("scroll_depth", { pct: m });
      }
    }

    // Progreso de lectura relativo al artículo.
    const rect = article.getBoundingClientRect();
    const articleTop = rect.top + window.scrollY;
    const articleH = article.offsetHeight || 1;
    const progress = Math.min(
      100,
      Math.max(0, Math.round(((scrolled - articleTop) / articleH) * 100))
    );
    if (progress > ctx.state.readingProgressPct) {
      const prevDecile = Math.floor(ctx.state.readingProgressPct / 10);
      // Estado CONTINUO (cada ~1%): lo leen las reglas y el objetivo A/B reading_progress (que se
      // evalúa en experiments.tick, no contra la emisión) → su precisión NO depende del coalescing.
      ctx.state.readingProgressPct = progress;
      // Coalescing de la EMISIÓN: solo al cruzar un NUEVO decil → ~10 filas/sesión en vez de ~100,
      // y nunca deja en cero a una sesión que alcanzó ≥10% (emite en el primer cruce). En SPA,
      // resetForPage pone readingProgressPct=0 → el conteo de deciles se reinicia solo, sin estado
      // extra que limpiar en reset().
      if (Math.floor(progress / 10) > prevDecile) {
        emit("reading_progress", { pct: progress });
      }
      if (progress >= 90 && !firedScroll.has(-1)) {
        firedScroll.add(-1);
        emit("read_complete", {});
      }
    }

    ctx.touch();
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(measure);
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });

  // section_view: H2/H3 dentro del artículo (re-observable al cambiar de página SPA).
  function observeHeadings() {
    if (!("IntersectionObserver" in window)) return;
    if (headingIO) headingIO.disconnect();
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          const el = e.target as HTMLElement;
          const id = el.id || el.innerText?.slice(0, 40) || "section";
          if (!ctx.state.sectionsViewed[id]) {
            ctx.state.sectionsViewed[id] = true;
            emit("section_view", { section: id });
            ctx.touch();
          }
          io.unobserve(el);
        }
      },
      { threshold: 0.6 }
    );
    article.querySelectorAll<HTMLElement>("h2, h3").forEach((h) => io.observe(h));
    headingIO = io;
  }

  measure(); // medición inicial
  observeHeadings();

  /**
   * Re-inicia las señales de lectura para una nueva página virtual (SPA): re-detecta
   * el artículo, olvida los milestones de scroll ya disparados y re-observa los
   * encabezados de la página nueva. Lo llama index.ts en cada page view virtual.
   */
  function reset() {
    article = detectArticle();
    firedScroll = new Set<number>();
    measure(); // recalcula scroll/progress contra el nuevo artículo
    observeHeadings();
  }

  return { reset };
}
